import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mic,
  StopCircle,
} from 'lucide-react';
import {
  buildHardwareDetails,
  createUuid,
  DEFAULT_WS_URL,
  float32ToPcm16,
  resampleBuffer,
  TARGET_RATE,
} from './audioMatcherUtils';
import ResultSegment from './ResultSegment';
import './AudioMatcher.css';

const MODEL_ID = 'W2V2v1.2';
const AUDIO_BUFFER_SIZE = 4096;

function getWorkletUrl() {
  return `${process.env.PUBLIC_URL || ''}/pcm-audio-worklet.js`;
}

function getMicrophoneUnavailableMessage() {
  if (!window.isSecureContext) {
    return 'Microphone capture requires HTTPS when this UI is opened from a server. Use an HTTPS URL, or run it from localhost during development.';
  }
  return 'Microphone capture is not available in this browser.';
}

export default function AudioMatcher({
  selectedGameTag,
}) {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [segments, setSegments] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [preprocessEnabled, setPreprocessEnabled] = useState(true);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const muteRef = useRef(null);
  const streamRef = useRef(null);
  const metaSentRef = useRef(false);
  const lastSendTimeRef = useRef(null);
  const audioStartedRef = useRef(false);
  const controlReadyRef = useRef(false);
  const expectedCloseRef = useRef(false);

  const userid = useMemo(() => createUuid(), []);
  const sessionid = useMemo(() => createUuid(), []);
  const FrontendDT = useMemo(() => new Date().toString(), []);

  const cleanupResources = () => {
    metaSentRef.current = false;
    lastSendTimeRef.current = null;
    audioStartedRef.current = false;
    controlReadyRef.current = false;

    try { processorRef.current && (processorRef.current.onaudioprocess = null); } catch {}
    try { processorRef.current && (processorRef.current.port.onmessage = null); } catch {}
    try { processorRef.current?.disconnect(); } catch {}
    try { muteRef.current?.disconnect(); } catch {}
    try { sourceRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    try { wsRef.current?.close(); } catch {}
    try { (streamRef.current?.getTracks?.() || []).forEach(track => track.stop()); } catch {}

    processorRef.current = null;
    muteRef.current = null;
    sourceRef.current = null;
    audioCtxRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  useEffect(() => cleanupResources, []);

  const addInfoSegment = raw => {
    setSegments(prev => [
      {
        id: Date.now() + Math.random(),
        rawData: raw,
        predicted: '',
        transcript: null,
        accuracy: null,
        matches: [],
        errors: [],
        latency: null,
        pitch: null,
        isChild: null,
        loudness: null,
        errorRegion: null,
        repetitions: null,
        speechRate: null,
        status: raw?.status || raw?.error || 'info',
        expected: '',
        errorAnalysisTarget: '',
        chunkId: null,
        modelId: null,
        processingMode: null,
        deviceAudio: null,
        actionWordLabel: null,
        greedyPathConfidence: null,
        perPhonemeConfidence: [],
        isInfo: true,
      },
      ...prev,
    ]);
  };

  const normalizeSegment = data => {
    const phonemeArr = Array.isArray(data.predicted_phonemes) ? data.predicted_phonemes : [];
    const expectedArr = Array.isArray(data.expected_phonemes) ? data.expected_phonemes : [];
    const errorAnalysisArr = Array.isArray(data.error_analysis_phonemes)
      ? data.error_analysis_phonemes
      : [];
    const matches = Array.isArray(data.matches) ? data.matches : [];
    const errors = Array.isArray(data.errors)
      ? data.errors.filter(error => error && typeof error === 'object')
      : [];

    return {
      id: Date.now() + Math.random(),
      rawData: data,
      predicted: phonemeArr.join(' '),
      expected: expectedArr.join(' '),
      errorAnalysisTarget: errorAnalysisArr.join(' '),
      transcript: data.transcript || data.text || data.predicted_text || null,
      accuracy: typeof data.weighted_accuracy === 'number' ? data.weighted_accuracy : null,
      matches,
      errors,
      latency: lastSendTimeRef.current ? Date.now() - lastSendTimeRef.current : null,
      pitch: data.pitch_hz ?? null,
      isChild: data.is_child_voice ?? null,
      loudness:
        typeof data.loudness_db_weighted === 'number' ? Math.round(data.loudness_db_weighted) : null,
      errorRegion: data.error_region ?? null,
      repetitions: Array.isArray(data.repetitions_detected)
        ? data.repetitions_detected.length
        : data.repetitions_detected ?? null,
      speechRate: data.speech_rate ?? null,
      status: data.status || 'ok',
      chunkId: data.chunk_id || data.timestamp || null,
      modelId: data.model_id || data.model || MODEL_ID,
      processingMode: data.processing_mode || null,
      deviceAudio: typeof data.device_audio === 'boolean' ? data.device_audio : null,
      actionWordLabel: data.action_word_label || null,
      greedyPathConfidence: data.greedy_path_confidence ?? null,
      perPhonemeConfidence: Array.isArray(data.per_phoneme_confidence) ? data.per_phoneme_confidence : [],
      isInfo: false,
    };
  };

  const start = async () => {
    if (starting || recording) return;

    expectedCloseRef.current = false;
    setStarting(true);
    setSpeaking(false);
    setSegments([]);

    const ws = new WebSocket(DEFAULT_WS_URL);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    const startAudioCapture = async () => {
      if (audioStartedRef.current) return;
      audioStartedRef.current = true;

      let audioCtx;
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = audioCtx;
        if (audioCtx.state === 'suspended') {
          try { await audioCtx.resume(); } catch {}
        }
      } catch (error) {
        addInfoSegment({ error: `Audio context error: ${String(error)}` });
        cleanupResources();
        setStarting(false);
        setRecording(false);
        setConnected(false);
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        addInfoSegment({ error: getMicrophoneUnavailableMessage() });
        cleanupResources();
        setStarting(false);
        setRecording(false);
        setConnected(false);
        return;
      }

      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        addInfoSegment({ error: `Mic permission denied: ${String(error)}` });
        cleanupResources();
        setStarting(false);
        setRecording(false);
        setConnected(false);
        return;
      }

      sourceRef.current = audioCtx.createMediaStreamSource(streamRef.current);
      muteRef.current = audioCtx.createGain();
      muteRef.current.gain.value = 0;

      // Continuous frame streaming: ship every captured frame straight to the
      // server and let the backend VAD decide where speech starts/ends. No
      // client-side voice detection or buffering -- that's what made the old
      // path slow (it waited for a 2.5s blob before sending anything).
      const sendAudioFrame = input => {
        if (!controlReadyRef.current || ws.readyState !== WebSocket.OPEN) return;

        const resampled = resampleBuffer(input, audioCtx.sampleRate, TARGET_RATE);
        if (resampled.length === 0) return;
        const pcm16 = float32ToPcm16(resampled);
        const chunkMs = Math.round((resampled.length / TARGET_RATE) * 1000);

        try {
          // Sticky meta: send the format descriptor once; the backend reuses it
          // for the continuous binary frames that follow.
          if (!metaSentRef.current) {
            ws.send(JSON.stringify({
              type: 'chunk_meta',
              device_audio: false,
              sample_rate: TARGET_RATE,
              channels: 1,
              format: 'pcm16_le',
              chunk_ms: chunkMs,
            }));
            metaSentRef.current = true;
          }
          ws.send(pcm16.buffer);
          lastSendTimeRef.current = Date.now();
        } catch {}
      };

      const startLegacyProcessor = () => {
        processorRef.current = audioCtx.createScriptProcessor(AUDIO_BUFFER_SIZE, 1, 1);
        processorRef.current.onaudioprocess = event => {
          sendAudioFrame(event.inputBuffer.getChannelData(0));
        };
        sourceRef.current.connect(processorRef.current);
        processorRef.current.connect(muteRef.current);
      };

      const startWorkletProcessor = async () => {
        if (!audioCtx.audioWorklet || typeof AudioWorkletNode === 'undefined') {
          return false;
        }

        await audioCtx.audioWorklet.addModule(getWorkletUrl());
        const workletNode = new AudioWorkletNode(audioCtx, 'pcm-audio-capture-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          processorOptions: { bufferSize: AUDIO_BUFFER_SIZE },
        });

        workletNode.port.onmessage = event => {
          if (event.data instanceof Float32Array) {
            sendAudioFrame(event.data);
          }
        };

        processorRef.current = workletNode;
        sourceRef.current.connect(workletNode);
        workletNode.connect(muteRef.current);
        return true;
      };

      try {
        const workletStarted = await startWorkletProcessor();
        if (!workletStarted) startLegacyProcessor();
      } catch (error) {
        addInfoSegment({ status: `AudioWorklet unavailable; using fallback processor. ${String(error)}` });
        startLegacyProcessor();
      }

      muteRef.current.connect(audioCtx.destination);

      setRecording(true);
      setStarting(false);
    };

    ws.onopen = () => {
      const payload = {
        gameid: selectedGameTag || 'test001',
        parentGameID: selectedGameTag || 'default',
        userid,
        sessionid,
        FrontendDT,
        appVersion: 'phoneme-match-ui',
        hardware_details: buildHardwareDetails(TARGET_RATE),
        model: MODEL_ID,
        preprocess_audio_enabled: preprocessEnabled,
      };

      ws.send(JSON.stringify(payload));
    };

    ws.onmessage = event => {
      let data = event.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
        else if (data instanceof ArrayBuffer) {
          const text = new TextDecoder('utf-8').decode(new Uint8Array(data));
          data = JSON.parse(text);
        }
      } catch {
        addInfoSegment({ error: 'Non-JSON message received.' });
        return;
      }

      if (data?.error) {
        addInfoSegment(data);
        if (!controlReadyRef.current) {
          setStarting(false);
          setConnected(false);
        }
        return;
      }

      if (data?.status === 'control parameters set') {
        controlReadyRef.current = true;
        setConnected(true);
        startAudioCapture();
        return;
      }

      if (data?.status === 'chunk_meta_set') return;

      // Continuous streaming emits a VAD status for every non-finalizing frame
      // (~12/sec). Don't list them — just drive the live speaking indicator.
      if (data?.status === 'streaming_vad_chunk_received') {
        setSpeaking(Boolean(data?.streaming_vad?.speech));
        return;
      }
      if (data?.status === 'speech_discarded' || data?.status === 'no_active_utterance') {
        setSpeaking(false);
        return;
      }

      if (data?.status === 'no_human_speech_detected') {
        addInfoSegment(data);
        return;
      }

      const hasResult =
        Array.isArray(data.predicted_phonemes) ||
        data.expected_phonemes ||
        (Array.isArray(data.errors) && data.errors.length > 0) ||
        data.weighted_accuracy != null ||
        data.status === 'device_audio_ignored';

      if (!hasResult) {
        if (data?.status) addInfoSegment(data);
        return;
      }

      setSpeaking(false);
      const newSeg = normalizeSegment(data);
      setSegments(prev => [newSeg, ...prev]);
    };

    ws.onclose = event => {
      if (!expectedCloseRef.current && (controlReadyRef.current || audioStartedRef.current)) {
        const closeCode = event?.code ? `code ${event.code}` : 'no close code';
        const closeReason = event?.reason ? `: ${event.reason}` : '';
        addInfoSegment({
          error: `WebSocket closed unexpectedly (${closeCode})${closeReason}. Endpoint: ${DEFAULT_WS_URL}`,
        });
      }
      setConnected(false);
      setRecording(false);
      setStarting(false);
    };

    ws.onerror = () => {
      addInfoSegment({ error: 'WebSocket connection error.' });
      setStarting(false);
    };
  };

  const stop = () => {
    expectedCloseRef.current = true;
    // Flush any utterance the backend VAD is still holding so the final word
    // isn't dropped, then tear down.
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'end_stream' }));
      }
    } catch {}
    cleanupResources();
    setRecording(false);
    setConnected(false);
    setStarting(false);
    setSpeaking(false);
  };

  const toggleJson = id => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyJson = rawData => {
    navigator.clipboard?.writeText(JSON.stringify(rawData, null, 2)).catch(() => {});
  };

  const visibleSegments = segments.filter(segment => (
    segment.isInfo ||
    segment.predicted ||
    segment.transcript ||
    segment.expected ||
    segment.status
  ));

  return (
    <div className="app-card">
      <div className="header-row">
        <h2 className="title">Phoneme Matcher</h2>

        <div className="controls-right">
          <div className={`model-pill fixed-model ${connected ? 'disabled' : ''}`}>
            <span className="model-pill-label">{MODEL_ID}</span>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={preprocessEnabled}
            className={`toggle-switch ${preprocessEnabled ? 'on' : 'off'} ${connected ? 'disabled' : ''}`}
            onClick={() => !connected && setPreprocessEnabled(value => !value)}
            disabled={connected}
            title="Preprocessing"
          >
            <span className="toggle-track">
              <span className="toggle-thumb" />
            </span>
            <span className="toggle-label">Preprocessing</span>
          </button>
        </div>
      </div>

      <div className="audio-matcher">
        <div className="buttons">
          <motion.button onClick={start} disabled={recording || starting} className="start-btn">
            <Mic size={18} /> {starting ? 'Starting...' : 'Start'}
          </motion.button>
          <motion.button onClick={stop} disabled={!recording && !starting} className="stop-btn">
            <StopCircle size={18} /> Stop
          </motion.button>
        </div>

        <div className={`status ${connected ? 'on' : 'off'} ${connected && speaking ? 'speaking' : ''}`}>
          {connected
            ? `${speaking ? 'Speaking…' : 'Listening…'} • ${selectedGameTag || 'No tag selected'}`
            : starting
              ? 'Connecting to API...'
              : 'Disconnected'}
        </div>

        <div className="segments">
          {visibleSegments.length === 0 ? (
            <div className="segment-empty">
              {connected ? 'Listening... speak to see predictions here.' : 'Press Start to connect and speak.'}
            </div>
          ) : (
            visibleSegments.map((segment, index) => {
              const isExpanded = expandedIds.has(segment.id);
              return (
                <ResultSegment
                  key={segment.id}
                  segment={segment}
                  index={index}
                  totalSegments={visibleSegments.length}
                  isExpanded={isExpanded}
                  toggleJson={toggleJson}
                  copyJson={copyJson}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
